import { useEffect } from 'react';

interface SchemaObject {
  '@context'?: string;
  '@type': string;
  [key: string]: unknown;
}

interface SEOProps {
  title: string;
  description: string;
  canonicalPath?: string;
  keywords?: string;
  ogType?: string;
  schemas?: SchemaObject[];
}

const SITE_URL: string = import.meta.env.VITE_SITE_URL || 'https://litbuyspreadsheets.online';

export { SITE_URL };

export default function SEO({ title, description, canonicalPath, keywords, ogType = 'article', schemas }: SEOProps) {
  useEffect(() => {
    document.title = title;

    const siteUrl = SITE_URL;
    const canonicalUrl = canonicalPath ? `${siteUrl}${canonicalPath}` : siteUrl;

    const upsertMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    upsertMeta('description', description);
    if (keywords) upsertMeta('keywords', keywords);

    upsertMeta('og:title', title, true);
    upsertMeta('og:description', description, true);
    upsertMeta('og:url', canonicalUrl, true);
    upsertMeta('og:type', ogType, true);

    upsertMeta('twitter:title', title);
    upsertMeta('twitter:description', description);

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    const existingScripts = document.querySelectorAll('script[data-seo-schema]');
    existingScripts.forEach((s) => s.remove());

    if (schemas && schemas.length > 0) {
      schemas.forEach((schema) => {
        const script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.setAttribute('data-seo-schema', '');
        script.textContent = JSON.stringify({
          '@context': 'https://schema.org',
          ...schema,
        });
        document.head.appendChild(script);
      });
    }

    return () => {
      const cleanupScripts = document.querySelectorAll('script[data-seo-schema]');
      cleanupScripts.forEach((s) => s.remove());
    };
  }, [title, description, canonicalPath, keywords, ogType, schemas]);

  return null;
}